import type {
  EntityId,
  HumanCommandSource,
  MatchState,
  PlayerId,
  Squad,
  SquadArchetype,
} from '@signalclash/shared';
import {
  SQUAD_DEFS,
  abilityForArchetype,
  secondaryAbilityForArchetype,
} from '@signalclash/shared';

import type { BuildMode } from '../input/BuildMode.js';
import type { InputRouter } from '../input/InputRouter.js';
import type { Selection, SelectionRef } from '../input/Selection.js';

// V5+ — production tile order mirrors HudPanel.PRODUCIBLE so the action-bar
// archetype layout matches the desktop command-card. Glyphs use the same
// table HudHelpers uses (ARCHETYPE_GLYPH) so the player learns one visual
// vocabulary across surfaces.
const PRODUCIBLE: readonly SquadArchetype[] = ['recon', 'assault', 'engineer', 'shield'];
const ARCHETYPE_GLYPH: Record<SquadArchetype, string> = {
  recon: '◇',
  assault: '⚔',
  engineer: '⚒',
  shield: '◈',
};

const BAR_ID = 'mobile-action-bar';
const STYLE_ID = 'mobile-action-bar-styles';

interface MobileActionBarOptions {
  selection: Selection;
  buildMode: BuildMode;
  human: HumanCommandSource;
  // The bar drives the InputRouter through its trigger* hooks rather than
  // re-implementing keyboard handlers. Late-bound — GameScene constructs the
  // router after this overlay exists, so the bar accepts a getter that
  // resolves the current router (or null before the scene is up).
  getRouter: () => InputRouter | null;
  playerId: PlayerId;
}

// V3 mobile: persistent on-screen action bar pinned to the bottom of the
// viewport. It exposes Build, Q-ability, E-ability, and a Home camera-reset
// — every gameplay verb the player still needs after V1.7 dropped the
// explicit Attack-Move (Advance) and Stop verbs. Squads auto-engage anything
// in range via CombatSystem regardless of order kind, and "stop" is replaced
// by issuing a fresh Move at the squad's current position.
// Hidden on wide desktop viewports via the CSS @media rule.
//
// Deselect is intentionally NOT a button on the bar — touch-tap on empty
// ground (or on the already-selected entity) clears Core/Building selections
// via InputRouter.handleLeftClick, so an explicit ✕ chip would be redundant
// chrome that ate a thumb-slot without buying any new capability.
//
// Rule 2 (presentation only): the bar reads MatchState + Selection and emits
// intents through HumanCommandSource or the InputRouter's trigger* hooks —
// never mutates simulation state directly.
export class MobileActionBar {
  private opts: MobileActionBarOptions;
  private container: HTMLDivElement;
  private homeBtn: HTMLButtonElement;
  private buildBtn: HTMLButtonElement;
  // V5++ — Build button now displays the currently-armed kind (Hub / Turret /
  // idle) via its icon + label. Refs are captured so renderState can mutate
  // them in place each tick without re-querying.
  private buildBtnIcon!: HTMLSpanElement;
  private buildBtnLabel!: HTMLSpanElement;
  private abilityBtn: HTMLButtonElement;
  private abilityBtnLabel: HTMLSpanElement;
  private abilityBtnCooldown: HTMLSpanElement;
  private secondaryBtn: HTMLButtonElement;
  private secondaryBtnLabel: HTMLSpanElement;
  private secondaryBtnCooldown: HTMLSpanElement;
  // V5+ — production tiles that swap in for Advance/Q/E/Stop when the player
  // has their own Core (or own operational Hub) selected. One per archetype;
  // visibility is toggled in renderState based on getSelectedProducer().
  private produceBtns: Map<SquadArchetype, {
    btn: HTMLButtonElement;
    meta: HTMLSpanElement;
  }> = new Map();
  private state: MatchState | null = null;
  private selectionRef: SelectionRef | null = null;
  private unsubscribeSelection: (() => void) | null = null;
  private unsubscribeBuild: (() => void) | null = null;

  constructor(opts: MobileActionBarOptions) {
    this.opts = opts;
    injectStyles();
    this.container = ensureDiv(BAR_ID, 'mab');

    // Home: center camera on own Core. Compact icon-only button.
    this.homeBtn = makeIconBtn('mab__btn mab__btn--home', '⌂', 'Center camera on Core');
    // V5++ — Build button has three visual states driven by buildMode.active:
    //   idle:   ⊞ "Build"   (amber accent)
    //   Hub:    ⊞ "Hub →"   (amber accent, armed)
    //   Turret: ⛉ "Turret →" (red-orange accent, armed)
    // The icon + label refs are captured so renderState can swap them in
    // place each tick. The button itself wears `data-armed-kind` so CSS can
    // paint per-kind accents.
    this.buildBtn = document.createElement('button');
    this.buildBtn.type = 'button';
    this.buildBtn.className = 'mab__btn mab__btn--build';
    this.buildBtn.setAttribute('aria-label', 'Build (cycle: Hub → Turret → off)');
    this.buildBtnIcon = document.createElement('span');
    this.buildBtnIcon.className = 'mab__icon';
    this.buildBtnIcon.setAttribute('aria-hidden', 'true');
    this.buildBtnIcon.textContent = '⊞';
    this.buildBtnLabel = document.createElement('span');
    this.buildBtnLabel.className = 'mab__label';
    this.buildBtnLabel.textContent = 'Build';
    this.buildBtn.appendChild(this.buildBtnIcon);
    this.buildBtn.appendChild(this.buildBtnLabel);
    const abilityWrap = makeAbilityBtn('Q');
    this.abilityBtn = abilityWrap.btn;
    this.abilityBtnLabel = abilityWrap.label;
    this.abilityBtnCooldown = abilityWrap.cooldown;
    const secondaryWrap = makeAbilityBtn('E');
    this.secondaryBtn = secondaryWrap.btn;
    this.secondaryBtnLabel = secondaryWrap.label;
    this.secondaryBtnCooldown = secondaryWrap.cooldown;

    this.container.appendChild(this.homeBtn);
    this.container.appendChild(this.buildBtn);
    this.container.appendChild(this.abilityBtn);
    this.container.appendChild(this.secondaryBtn);

    // V5+ — production tiles. Appended AFTER the action buttons so the
    // visible flex-row when production-mode is active reads:
    // [Home] [Build] [Recon] [Assault] [Engineer] [Shield]
    // (action buttons in slots 3-6 are display:none in that mode). The
    // archetype glyph mirrors the in-game render, the meta line shows
    // cost / build-time so the player has the same affordances as the
    // desktop HudPanel without having to read a secondary panel.
    for (const archetype of PRODUCIBLE) {
      const def = SQUAD_DEFS[archetype];
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = `mab__btn mab__btn--produce mab__btn--produce-${archetype}`;
      btn.dataset.archetype = archetype;
      btn.setAttribute('aria-label', `Produce ${archetype}`);
      const iconEl = document.createElement('span');
      iconEl.className = 'mab__icon';
      iconEl.setAttribute('aria-hidden', 'true');
      iconEl.textContent = ARCHETYPE_GLYPH[archetype];
      const labelEl = document.createElement('span');
      labelEl.className = 'mab__label';
      labelEl.textContent = capitalize(archetype);
      const metaEl = document.createElement('span');
      metaEl.className = 'mab__meta';
      metaEl.textContent = `${def.cost}S`;
      btn.appendChild(iconEl);
      btn.appendChild(labelEl);
      btn.appendChild(metaEl);
      btn.style.display = 'none';
      btn.addEventListener('click', () => this.getRouter()?.triggerProduce(archetype));
      this.container.appendChild(btn);
      this.produceBtns.set(archetype, { btn, meta: metaEl });
    }

    this.homeBtn.addEventListener('click', () => this.getRouter()?.triggerHome());
    // V5++ — Build button now CYCLES: idle → Hub → Turret → idle on
    // repeated taps. Lets touch players pick Hub vs Turret without leaving
    // the bar. Visual updates per cycle step in renderState (icon + label +
    // accent colour reflect the currently-armed kind).
    this.buildBtn.addEventListener('click', () => this.getRouter()?.triggerBuildCycle());
    this.abilityBtn.addEventListener('click', () => this.getRouter()?.triggerPrimaryAbility());
    this.secondaryBtn.addEventListener('click', () => this.getRouter()?.triggerSecondaryAbility());

    this.unsubscribeSelection = opts.selection.subscribe((sel) => {
      this.selectionRef = sel;
      this.renderState();
    });
    this.unsubscribeBuild = opts.buildMode.subscribe(() => this.renderState());
    this.hide();
  }

  setPlayerId(playerId: PlayerId): void {
    this.opts = { ...this.opts, playerId };
    this.renderState();
  }

  show(): void {
    this.container.style.display = '';
    this.renderState();
  }

  hide(): void {
    this.container.style.display = 'none';
  }

  update(state: MatchState): void {
    this.state = state;
    this.renderState();
  }

  dispose(): void {
    this.unsubscribeSelection?.();
    this.unsubscribeBuild?.();
    this.unsubscribeSelection = null;
    this.unsubscribeBuild = null;
    this.container.remove();
  }

  private getRouter(): InputRouter | null {
    return this.opts.getRouter();
  }

  // Enable / disable per current selection + cooldowns. The button copy also
  // changes — e.g., the Q button shows the ability name + cooldown seconds so
  // the player doesn't have to read the small HUD context panel to decide
  // whether to fire. Build / Advance / Stop reflect armed-state via dataset so
  // CSS can paint them as "currently armed".
  //
  // V5+ — contextual swap: when the player has their own Core (or own
  // operational Production Hub) selected, the bar replaces the Q/E tail
  // with four production tiles. The ability verbs are dead in that
  // selection-mode anyway (no squad selected = nothing to fire an ability
  // on), so swapping them for production tiles puts the most relevant
  // verbs under the player's thumb without growing the bar.
  private renderState(): void {
    const router = this.getRouter();
    const armedKind = this.opts.buildMode.getActive();
    const buildArmed = armedKind !== null;
    this.buildBtn.dataset.armed = String(buildArmed);
    // V5++ — `data-armed-kind` lets CSS paint Hub (amber) and Turret (red)
    // with distinct accents so the cycle step is visually obvious. The
    // icon + label swap in here mirrors the BuildPanel / HudPanel build
    // tiles so the player learns one visual vocabulary across surfaces.
    if (armedKind === 'production_hub') {
      this.buildBtn.dataset.armedKind = 'production_hub';
      this.buildBtnIcon.textContent = '⊞';
      this.buildBtnLabel.textContent = 'Hub →';
    } else if (armedKind === 'turret') {
      this.buildBtn.dataset.armedKind = 'turret';
      this.buildBtnIcon.textContent = '⛉';
      this.buildBtnLabel.textContent = 'Turret →';
    } else {
      this.buildBtn.dataset.armedKind = '';
      this.buildBtnIcon.textContent = '⊞';
      this.buildBtnLabel.textContent = 'Build';
    }
    // Build is always allowed (cycles default).
    this.buildBtn.disabled = false;
    // Home always enabled.
    this.homeBtn.disabled = false;

    // Contextual swap: production mode when player has their own producer
    // selected, action mode otherwise. The producer query is router-side so
    // the "is the Hub operational / has it taken over from the Core" rules
    // stay in one place (mirror of HudPanel.renderCore).
    const producer = router?.getSelectedProducer() ?? null;
    if (producer !== null) {
      this.applyProductionMode(producer.producerId);
    } else {
      this.applyActionMode();
    }
  }

  // Production mode: hide Q/E ability buttons, show the four production
  // tiles with per-archetype affordability. Mirrors HudPanel.renderCore /
  // renderBuilding's enable rules so a button that's disabled here is also
  // disabled in the desktop card (and vice versa).
  private applyProductionMode(producerId: EntityId): void {
    this.abilityBtn.style.display = 'none';
    this.secondaryBtn.style.display = 'none';
    const state = this.state;
    if (state === null) {
      for (const ref of this.produceBtns.values()) {
        ref.btn.style.display = '';
        ref.btn.disabled = true;
        ref.btn.title = '';
      }
      return;
    }
    const player = state.players[this.opts.playerId];
    // Resolve the producer's queue length so the squad-cap check matches the
    // server-side rule (player.squadCount + queued ≤ cap).
    let queueLen = 0;
    let producerHasSlot = true;
    const core = state.cores[producerId];
    if (core !== undefined) {
      producerHasSlot = core.produces !== null;
      queueLen = core.produces?.queue.length ?? 0;
    } else {
      const building = state.buildings[producerId];
      if (building !== undefined && building.kind === 'production_hub') {
        queueLen = building.produces.queue.length;
      }
    }
    for (const archetype of PRODUCIBLE) {
      const ref = this.produceBtns.get(archetype);
      if (ref === undefined) continue;
      ref.btn.style.display = '';
      const def = SQUAD_DEFS[archetype];
      const cantAfford = player.signal < def.cost;
      const blockedCap = player.squadCount + queueLen >= player.squadCap;
      const blockedSlot = !producerHasSlot;
      ref.btn.disabled = cantAfford || blockedCap || blockedSlot;
      ref.btn.title = blockedSlot
        ? 'Production unavailable'
        : blockedCap
          ? 'Squad cap reached'
          : cantAfford
            ? `Need ${def.cost} Signal`
            : `${def.cost}S · ${def.buildTimeSec}s`;
    }
  }

  // Action mode: the Q/E ability layout, with cooldown / ability-name
  // labels driven by the currently-selected squad (or disabled when no own
  // squad is selected). V1.7 dropped the explicit Advance / Stop buttons —
  // squads auto-engage in-range enemies (CombatSystem) on Move orders and
  // re-issuing Move at the current position stops a squad.
  private applyActionMode(): void {
    this.abilityBtn.style.display = '';
    this.secondaryBtn.style.display = '';
    for (const ref of this.produceBtns.values()) {
      ref.btn.style.display = 'none';
    }
    const squad = this.selectedOwnSquad();
    if (squad === null) {
      this.abilityBtn.disabled = true;
      this.abilityBtn.dataset.ready = 'false';
      this.abilityBtnLabel.textContent = 'Ability';
      this.abilityBtnCooldown.textContent = '';
      this.secondaryBtn.disabled = true;
      this.secondaryBtn.dataset.ready = 'false';
      this.secondaryBtnLabel.textContent = 'Ability E';
      this.secondaryBtnCooldown.textContent = '';
      this.secondaryBtn.style.visibility = 'hidden';
      return;
    }
    const primaryDef = abilityForArchetype(squad.archetype);
    this.abilityBtnLabel.textContent = primaryDef.name;
    const cd = Math.max(0, squad.abilityCooldown);
    if (cd > 0.05) {
      this.abilityBtnCooldown.textContent = `${cd.toFixed(1)}s`;
      this.abilityBtn.disabled = true;
      this.abilityBtn.dataset.ready = 'false';
    } else {
      this.abilityBtnCooldown.textContent = 'Q';
      this.abilityBtn.disabled = false;
      this.abilityBtn.dataset.ready = 'true';
    }
    const secondary = secondaryAbilityForArchetype(squad.archetype);
    if (secondary === null) {
      this.secondaryBtn.style.visibility = 'hidden';
      this.secondaryBtn.disabled = true;
    } else {
      this.secondaryBtn.style.visibility = 'visible';
      this.secondaryBtnLabel.textContent = secondary.name;
      const cd2 = Math.max(0, squad.secondaryAbilityCooldown);
      if (cd2 > 0.05) {
        this.secondaryBtnCooldown.textContent = `${cd2.toFixed(1)}s`;
        this.secondaryBtn.disabled = true;
        this.secondaryBtn.dataset.ready = 'false';
      } else {
        this.secondaryBtnCooldown.textContent = 'E';
        this.secondaryBtn.disabled = false;
        this.secondaryBtn.dataset.ready = 'true';
      }
    }
  }

  private selectedOwnSquad(): Squad | null {
    const sel = this.selectionRef;
    const state = this.state;
    if (sel === null || state === null) return null;
    if (sel.kind !== 'squad') return null;
    const squad = state.squads[sel.id];
    if (squad === undefined) return null;
    if (squad.ownerId !== this.opts.playerId) return null;
    return squad;
  }
}

// Compact icon-only button for utility actions (Home) that need minimal
// horizontal footprint in the bar.
function makeIconBtn(cls: string, icon: string, ariaLabel: string): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = cls;
  btn.setAttribute('aria-label', ariaLabel);
  const iconEl = document.createElement('span');
  iconEl.className = 'mab__icon';
  iconEl.textContent = icon;
  btn.appendChild(iconEl);
  return btn;
}

function makeAbilityBtn(defaultHotkey: string): {
  btn: HTMLButtonElement;
  label: HTMLSpanElement;
  cooldown: HTMLSpanElement;
} {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'mab__btn mab__btn--ability';
  const label = document.createElement('span');
  label.className = 'mab__label';
  label.textContent = 'Ability';
  const cooldown = document.createElement('span');
  cooldown.className = 'mab__hotkey';
  cooldown.textContent = defaultHotkey;
  btn.appendChild(label);
  btn.appendChild(cooldown);
  return { btn, label, cooldown };
}

// V5+ — local copy of the hudHelpers capitalize so MobileActionBar doesn't
// pull from the HUD-DOM-coupled helpers file (which would drag in transitive
// HUD-only imports). One-line helper; not worth a shared utility module.
function capitalize(s: string): string {
  return s.length === 0 ? s : s[0]!.toUpperCase() + s.slice(1);
}

function ensureDiv(id: string, className: string): HTMLDivElement {
  const existing = document.getElementById(id);
  if (existing instanceof HTMLDivElement) {
    existing.className = className;
    return existing;
  }
  const div = document.createElement('div');
  div.id = id;
  div.className = className;
  document.body.appendChild(div);
  return div;
}

function injectStyles(): void {
  if (document.getElementById(STYLE_ID) !== null) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
#${BAR_ID} {
  position: fixed;
  left: 50%;
  bottom: 8px;
  transform: translateX(-50%);
  display: none;
  z-index: 18;
  gap: 6px;
  padding: 6px 8px;
  background: rgba(8,12,18,0.82);
  border: 1px solid #2a3038;
  border-radius: 8px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  color: #cfd6dd;
  box-shadow: 0 8px 24px rgba(0,0,0,0.55);
  /* Avoid the iOS home-indicator overlapping the bar. */
  margin-bottom: env(safe-area-inset-bottom, 0);
  touch-action: manipulation;
}
.mab__btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  min-width: 56px;
  min-height: 52px;
  padding: 6px 8px;
  background: #1f262e;
  color: #eef3f8;
  border: 1px solid #3a444f;
  border-radius: 6px;
  font-family: inherit;
  font-size: 12px;
  letter-spacing: 0.02em;
  cursor: pointer;
  /* Snappier feel on iOS — kills the 300 ms tap delay leftovers. */
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
}
.mab__btn:active:not(:disabled) {
  background: #2c3a4a;
  transform: scale(0.96);
}
.mab__btn:disabled { opacity: 0.4; cursor: not-allowed; }
.mab__btn[data-armed="true"] {
  background: #2c3a4a;
  border-color: #f5e76e;
  color: #f5e76e;
  box-shadow: 0 0 8px rgba(245,231,110,0.35);
}
/* Compact utility button (Home) — narrower than action buttons. */
.mab__btn--home {
  min-width: 40px;
  padding: 6px 4px;
}
.mab__icon {
  font-size: 18px;
  line-height: 1;
}
.mab__label {
  font-size: 12px;
  text-align: center;
  /* Truncate long ability names so the button doesn't reflow the bar. */
  max-width: 96px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
/* The hotkey badge is only used for ability buttons where it also displays
 * the cooldown timer — for Build/Advance/Stop we removed the badge so touch
 * users aren't shown keyboard shortcut labels that are irrelevant to them. */
.mab__hotkey {
  font-size: 10px;
  letter-spacing: 0.08em;
  color: #f5e76e;
  border: 1px solid #f5e76e;
  border-radius: 3px;
  padding: 0 4px;
  min-width: 18px;
  text-align: center;
}
.mab__btn:disabled .mab__hotkey { color: #b8c0c8; border-color: #4a5564; }
.mab__btn--ability .mab__label { color: #ffe57b; }
.mab__btn--build[data-armed="true"] .mab__hotkey { color: #f5e76e; border-color: #f5e76e; }

/* ── Per-function colour accents ────────────────────────────────────────────
 * Build: amber/gold. V1.7 removed the Advance/Stop accents along with the
 * buttons themselves — squads auto-engage in-range enemies on plain Move so
 * the explicit Attack-Move and Stop verbs are no longer surfaced.
 */
.mab__btn--build:not([data-armed="true"]) { border-color: #4a4020; }
.mab__btn--build:not([data-armed="true"]):not(:disabled):hover {
  border-color: #c8b840; color: #f5e76e; background: #26221a;
}
/* V5++ — per-cycle-step accent for the Build button. Hub is amber/gold
 * (matches the in-card Hub tile), Turret is red-orange (matches the in-card
 * Turret tile). The base [data-armed="true"] rule still applies under these. */
.mab__btn--build[data-armed-kind="production_hub"] {
  background: #2c2818; border-color: #c8b840; color: #f5e76e;
  box-shadow: 0 0 8px rgba(245,231,110,0.4);
}
.mab__btn--build[data-armed-kind="turret"] {
  background: #2a1814; border-color: #e2543c; color: #ff9670;
  box-shadow: 0 0 8px rgba(226,84,60,0.4);
}

/* ── V5+ contextual production tiles ───────────────────────────────────────
 * Swapped in for Advance/Q/E/Stop when the player has their own Core or
 * own operational Production Hub selected. Each archetype gets its canonical
 * accent colour so the tile reads the same as the in-game squad and the
 * desktop HudPanel production button.
 */
.mab__btn--produce { gap: 1px; }
.mab__btn--produce .mab__icon { font-size: 20px; }
.mab__btn--produce .mab__label {
  font-size: 11px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}
.mab__meta {
  font-size: 9px;
  color: #8c95a0;
  letter-spacing: 0.04em;
}
.mab__btn--produce:disabled .mab__meta { color: #4a5564; }
.mab__btn--produce-recon    { border-color: #2a5070; color: #9ec8f0; }
.mab__btn--produce-recon:not(:disabled):hover {
  border-color: #6ad8ff; color: #6ad8ff; background: #14283c;
}
.mab__btn--produce-assault  { border-color: #60281a; color: #ff9670; }
.mab__btn--produce-assault:not(:disabled):hover {
  border-color: #e2543c; color: #ff8060; background: #2a1410;
}
.mab__btn--produce-engineer { border-color: #4a4010; color: #ffe680; }
.mab__btn--produce-engineer:not(:disabled):hover {
  border-color: #f5e76e; color: #f5e76e; background: #2a2410;
}
.mab__btn--produce-shield   { border-color: #1a5048; color: #6ed4c0; }
.mab__btn--produce-shield:not(:disabled):hover {
  border-color: #4ad0b8; color: #4ad0b8; background: #102822;
}

/* ── Ability ready-state pulse ──────────────────────────────────────────────
 * When an ability comes off cooldown the button pulses green so the player
 * notices without watching the cooldown bar.
 */
@keyframes mab-ready-pulse {
  0%, 100% { border-color: #4ad06b; box-shadow: 0 0 4px rgba(74,208,107,0.35); }
  50%       { border-color: #72f090; box-shadow: 0 0 12px rgba(74,208,107,0.65); }
}
.mab__btn--ability[data-ready="true"] {
  animation: mab-ready-pulse 1.5s ease-in-out infinite;
}
.mab__btn--ability[data-ready="true"] .mab__label { color: #72f090; }
.mab__btn--ability[data-ready="true"] .mab__hotkey { color: #4ad06b; border-color: #4ad06b; }

/* Show the bar only on mobile / coarse-pointer / narrow viewports. Desktop
 * mouse+keyboard play hides it so the HUD doesn't change for that audience. */
@media (max-width: 820px), (pointer: coarse) {
  #${BAR_ID} { display: flex; }
}

/* Narrow phones — let the 4 buttons share the available width and soft-wrap
 * to a second row as a final safety net on extremely narrow screens. V1.7
 * dropped Advance / Stop, so the bar now carries Home / Build / Q / E and
 * fits comfortably on a 320 px viewport with room to spare. */
@media (max-width: 600px) {
  #${BAR_ID} {
    left: 6px;
    right: 6px;
    transform: none;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 4px;
    padding: 4px 6px;
    max-width: calc(100vw - 12px);
    box-sizing: border-box;
  }
  .mab__btn {
    flex: 1 1 auto;
    /* Stay at the Apple HIG 44 px tap-target floor even when the bar
     * stretches; min-width relaxed so all buttons fit a 320 px viewport. */
    min-width: 38px;
    min-height: 44px;
    padding: 4px 4px;
  }
  .mab__btn--home {
    flex: 0 0 auto;
    min-width: 34px;
    padding: 4px 2px;
  }
  .mab__label { font-size: 10px; max-width: 78px; }
  .mab__icon { font-size: 16px; }
  /* Production tiles share the same compact rules so the contextual swap
   * doesn't reflow the bar between modes. */
  .mab__btn--produce .mab__icon { font-size: 17px; }
  .mab__btn--produce .mab__label { font-size: 10px; }
  .mab__meta { font-size: 8px; }
}

/* Landscape phones — short viewports. Cut bar height a little more so the
 * playable area is not crowded out by the bottom HUD strip. */
@media (orientation: landscape) and (max-height: 480px) and (pointer: coarse) {
  #${BAR_ID} { bottom: 4px; padding: 3px 6px; gap: 4px; }
  .mab__btn { min-height: 40px; padding: 3px 6px; }
  .mab__label { font-size: 10px; }
  .mab__btn--produce .mab__icon { font-size: 15px; }
  .mab__meta { font-size: 8px; }
}
`;
  document.head.appendChild(style);
}
